import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getAllTags } from '../../utils/RecipeService';
import { EMPTY_FILTERS, MatchFilters } from '../../utils/MatchService';

type Props = {
  open: boolean;
  initialFilters: MatchFilters;
  onClose: (filters: MatchFilters) => void;
};

const COOK_TIME_VALUES: (number | null)[] = [30, 60, 90, null];

const DIFFICULTY_OPTIONS = ['easy', 'medium', 'hard'];
const DIFFICULTY_ANY = '__any__';
const COOKTIME_NONE = '__none__';

const RecipeMatchFilterDialog = ({ open, initialFilters, onClose }: Props) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<MatchFilters>(EMPTY_FILTERS);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  // Reset draft to current filters whenever dialog opens
  useEffect(() => {
    if (open) {
      setDraft(initialFilters);
      getAllTags().then((tags: string[]) => setAvailableTags(tags.sort()));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTag = (tag: string) => {
    setDraft(prev => {
      const lower = tag.toLowerCase();
      const has = prev.tags.includes(lower);
      return { ...prev, tags: has ? prev.tags.filter(t => t !== lower) : [...prev.tags, lower] };
    });
  };

  const setMaxCookTime = (_: any, val: string) => {
    const parsed = val === COOKTIME_NONE || !val ? null : parseInt(val, 10);
    setDraft(prev => ({ ...prev, maxCookTime: parsed != null && isNaN(parsed) ? null : parsed }));
  };

  const setDifficulty = (_: any, val: string) => {
    setDraft(prev => ({ ...prev, difficulty: val === DIFFICULTY_ANY || !val ? null : val }));
  };

  return (
    <Dialog open={open} onClose={() => onClose(draft)} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>{t('match_addFilters')}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Tags */}
        <Box>
          <Typography variant="overline" color="text.secondary" fontWeight={600}>
            {t('match_filterTags')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {availableTags.length === 0 ? (
              <Typography variant="body2" color="text.disabled">{t('match_noTags')}</Typography>
            ) : availableTags.map(tag => {
              const selected = draft.tags.includes(tag.toLowerCase());
              return (
                <Chip
                  key={tag}
                  label={tag}
                  onClick={() => toggleTag(tag)}
                  variant={selected ? 'filled' : 'outlined'}
                  sx={selected ? {
                    bgcolor: '#fbe9d6', borderWidth: 1.5,
                    borderColor: 'primary.main', color: 'primary.dark', fontWeight: 600,
                  } : { borderWidth: 1.5 }}
                />
              );
            })}
          </Box>
        </Box>

        <Divider />

        {/* Max cook time */}
        <Box>
          <Typography variant="overline" color="text.secondary" fontWeight={600}>
            {t('match_maxCookTime')}
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={draft.maxCookTime === null ? COOKTIME_NONE : String(draft.maxCookTime)}
            onChange={setMaxCookTime}
            sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
          >
            {COOK_TIME_VALUES.map(val => (
              <ToggleButton
                key={val === null ? COOKTIME_NONE : String(val)}
                value={val === null ? COOKTIME_NONE : String(val)}
                sx={{ flex: '1 1 auto', fontSize: '0.8rem' }}
              >
                {val === null ? t('match_noLimit') : `${val} min`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Divider />

        {/* Difficulty */}
        <Box>
          <Typography variant="overline" color="text.secondary" fontWeight={600}>
            {t('difficulty')}
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={draft.difficulty ?? DIFFICULTY_ANY}
            onChange={setDifficulty}
            sx={{ mt: 1, display: 'flex' }}
          >
            {DIFFICULTY_OPTIONS.map(d => (
              <ToggleButton key={d} value={d} sx={{ flex: 1, fontSize: '0.8rem' }}>
                {t(d)}
              </ToggleButton>
            ))}
            <ToggleButton value={DIFFICULTY_ANY} sx={{ flex: 1, fontSize: '0.8rem' }}>
              {t('match_anyDifficulty')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() => onClose(draft)}
          sx={{ borderRadius: 8, fontWeight: 700 }}
        >
          {t('match_done')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipeMatchFilterDialog;
